import { useRef, useState, useCallback, useEffect } from 'react';

interface UseResizeHandleOptions {
  /** Initial size in pixels */
  initial: number;
  /** Minimum allowed size */
  min: number;
  /** Maximum allowed size */
  max: number;
  /** Resize direction — which axis the drag controls */
  direction: 'horizontal' | 'vertical';
  /** Whether dragging toward the start of the axis grows the panel (e.g. sidebar on right edge) */
  inverted?: boolean;
  /** localStorage key to persist size across sessions */
  storageKey?: string;
}

interface UseResizeHandleReturn {
  size: number;
  isResizing: boolean;
  /** Spread onto the drag handle element */
  handleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    style: React.CSSProperties;
  };
}

function readStoredSize(key: string | undefined, fallback: number, min: number, max: number): number {
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw != null) {
      const parsed = Number(raw);
      if (!isNaN(parsed)) return Math.max(min, Math.min(max, parsed));
    }
  } catch { /* localStorage unavailable */ }
  return fallback;
}

export function useResizeHandle({
  initial,
  min,
  max,
  direction,
  inverted = false,
  storageKey,
}: UseResizeHandleOptions): UseResizeHandleReturn {
  const [size, setSize] = useState(() => readStoredSize(storageKey, initial, min, max));
  const isResizingRef = useRef(false);
  const [isResizing, setIsResizing] = useState(false);
  const startPosRef = useRef(0);
  const startSizeRef = useRef(size);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      setIsResizing(true);
      startPosRef.current = direction === 'horizontal' ? e.clientX : e.clientY;
      startSizeRef.current = size;
      document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [direction, size]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const pos = direction === 'horizontal' ? e.clientX : e.clientY;
      const delta = pos - startPosRef.current;
      const newSize = inverted
        ? startSizeRef.current - delta
        : startSizeRef.current + delta;
      setSize(Math.max(min, Math.min(max, newSize)));
    };

    const onMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [direction, inverted, min, max]);

  // Persist to localStorage on resize end
  useEffect(() => {
    if (!isResizing && storageKey) {
      try { localStorage.setItem(storageKey, String(size)); } catch { /* noop */ }
    }
  }, [isResizing, size, storageKey]);

  const handleProps = {
    onMouseDown,
    style: {
      cursor: direction === 'horizontal' ? 'col-resize' : 'row-resize',
    } as React.CSSProperties,
  };

  return { size, isResizing, handleProps };
}
